import type { Dispatch, UnknownAction } from '@reduxjs/toolkit'

import { windowTitle, type WindowRegistry } from './registry'
import type { WindowMatch } from './types'
import {
  MAX_TABS,
  pathOfLocation,
  returnForgotten,
  returnRecorded,
  tabCreated,
  tabSaved,
  type WindowsState,
} from './windowsSlice'

/**
 * Переходы между окнами — перенос `windows.thunks.ts` старого клиента (карточки
 * 265, 274) поверх реестра нового. Навигацию здесь не делают: функция говорит,
 * КУДА идти, а переходит вызывающий (роутер в стор не тащим).
 *
 * Пакет не знает RootState, поэтому команды собираются фабрикой: composition
 * передаёт реестр и селектор слайса окон.
 */

/** Имя параметра адреса для окон поверх экрана: `?w=onec.product:<rid>/<key>`. */
export const WINDOW_PARAM = 'w'

/** Адрес без одной ссылки `?w=`: окна, которые остались поверх экрана. */
export function withoutRef(search: string, reference: string): string {
  const rest = new URLSearchParams(search)
  const others = rest.getAll(WINDOW_PARAM).filter((value) => value !== reference)
  rest.delete(WINDOW_PARAM)
  for (const value of others) rest.append(WINDOW_PARAM, value)
  const query = rest.toString()
  return query === '' ? '' : `?${query}`
}

/** Рабочее окно — то, у которого есть пункт меню, вход раздела или вкладка раздела. */
export function isWorkingDescriptor(descriptor: { menu?: unknown; section?: { home?: unknown; tab?: unknown } }): boolean {
  return descriptor.menu !== undefined || descriptor.section?.home !== undefined || descriptor.section?.tab !== undefined
}

/** Дом приложения: туда ведёт закрытие, если возвращаться больше некуда. */
export const HOME_PATH = '/'

/** Что известно о переходе, когда человек оказался на новом адресе. */
export interface WindowEntry {
  pathname: string
  /** Где человек был перед этим (путь с параметрами). `null` — первый переход (вход, F5). */
  previous: string | null
  /** Куда вернёт закрытие окна. Обычно `previous`; `proceedTo` называет его явно. */
  from: string | null
  /** `from` назвал сам переход (`windowFrom`), а не вывелся из прежнего адреса. */
  explicit?: boolean
  /** Возврат назад: «откуда пришли» у окна остаётся прежним. */
  back?: boolean
  /** Работу прежней вкладки уже сохранил тот, кто переключил. */
  tabSaved?: boolean
}

/** Состояние навигации, которое окна передают роутеру при переходе. */
export interface WindowNavigationState {
  windowFrom?: string | null
  windowBack?: boolean
  windowTabSaved?: boolean
}

type Thunk<R, T> = (dispatch: Dispatch<UnknownAction>, getState: () => R) => T

export interface WindowNavigation<R> {
  enterWindow(entry: WindowEntry): Thunk<R, boolean>
  closeWindow(location: string): Thunk<R, string>
  switchToTab(id: string, current: string): Thunk<R, string | null>
  createTab(location: string): Thunk<R, boolean>
}

/** Адрес без окон поверх экрана: вкладка ведёт на экран, а не в диалог над ним. */
export function withoutStack(location: string): string {
  const query = location.indexOf('?')
  if (query < 0) return location
  const params = new URLSearchParams(location.slice(query + 1))
  params.delete(WINDOW_PARAM)
  const rest = params.toString()
  return rest === '' ? location.slice(0, query) : `${location.slice(0, query)}?${rest}`
}

export function createWindowNavigation<R>(
  registry: WindowRegistry<R>,
  selectWindows: (root: R) => WindowsState,
  now: () => Date = () => new Date(),
): WindowNavigation<R> {
  const matchOf = (location: string): WindowMatch<R> | undefined => registry.match(pathOfLocation(location))

  /** Запомнить работу вкладки окна на `location`. Окно без вкладки — ничего. */
  function saveTab(dispatch: Dispatch<UnknownAction>, state: R, location: string): void {
    const match = matchOf(location)
    if (match === undefined) return
    const tab = selectWindows(state).tabs.find((item) => item.id === match.identity)
    if (tab === undefined) return
    dispatch(
      tabSaved({
        id: tab.id,
        location: withoutStack(location),
        title: windowTitle(match),
        snapshot: match.descriptor.capture?.(state, match.params) ?? null,
        at: now().toISOString(),
      }),
    )
  }

  /**
   * Явный переход между окнами (чип вкладки, закрытие): работа покидаемой
   * вкладки сохраняется, работа той, куда идут, возвращается. Сначала
   * сохранить, потом восстановить: окна делят слайсы.
   */
  function moveBetweenWindows(
    dispatch: Dispatch<UnknownAction>,
    getState: () => R,
    current: string,
    target: string,
  ): void {
    const entering = matchOf(target)
    if (entering === undefined || matchOf(current)?.identity === entering.identity) return
    saveTab(dispatch, getState(), current)
    const tab = selectWindows(getState()).tabs.find((item) => item.id === entering.identity)
    if (tab?.snapshot == null || entering.descriptor.restore === undefined) return
    for (const action of entering.descriptor.restore(tab.snapshot, entering.params)) dispatch(action)
  }

  return {
    /**
     * Человек оказался на адресе окна — неважно как. Вкладку переход НЕ
     * создаёт (карточка 274). Уход из окна вкладки сохраняет её работу, окно
     * запоминает, откуда в него пришли. `false` — адрес окном не обслуживается.
     */
    enterWindow: (entry) => (dispatch, getState) => {
      const window = registry.match(entry.pathname)
      if (
        entry.previous !== null &&
        entry.tabSaved !== true &&
        matchOf(entry.previous)?.identity !== window?.identity
      ) {
        saveTab(dispatch, getState(), entry.previous)
      }
      if (window === undefined) return false
      if (entry.back === true) return true
      // Рабочее окно (меню, вход и вкладка раздела) — равное среди равных: переход между такими окнами
      // («РМК» → «Чеки») ничьим «ребёнком» его не делает и «← Назад» не рождает. Назад есть у окон,
      // в которые ведёт действие: карточка, мастер, форма — и у рабочего окна, открытого действием
      // явно (`windowFrom`: «открыть чек из продажи»).
      const peer = isWorkingDescriptor(window.descriptor)
      const recordable = entry.explicit === true || !peer
      const origin = recordable && entry.from !== null ? matchOf(entry.from) : undefined
      if (origin !== undefined && origin.identity !== window.identity) {
        dispatch(returnRecorded({ window: window.identity, from: entry.from as string, resourceId: window.params.rid ?? null }))
      } else if (peer && entry.previous !== null && matchOf(entry.previous)?.identity !== window.identity) {
        // Пришли сюда не действием: прежний возврат (из давнего перехода) устарел — «Назад» не показывать.
        dispatch(returnForgotten(window.identity))
      }
      return true
    },

    /**
     * Закрыть окно и сказать, куда идти: туда, откуда пришли; иначе в последнюю
     * вкладку; иначе домой. Одно правило для «← назад», «Отмены» и «Готово».
     */
    closeWindow: (location) => (dispatch, getState) => {
      const window = matchOf(location)?.identity
      const state = selectWindows(getState())
      const back = window === undefined ? undefined : state.returns[window]?.from
      if (window !== undefined) dispatch(returnForgotten(window))
      const target = back ?? state.tabs.findLast((tab) => tab.id !== window)?.location ?? HOME_PATH
      moveBetweenWindows(dispatch, getState, location, target)
      return target
    },

    /** Перейти во вкладку чипом. `null` — человек уже в этом окне. */
    switchToTab: (id, current) => (dispatch, getState) => {
      const tab = selectWindows(getState()).tabs.find((item) => item.id === id)
      if (tab === undefined || matchOf(current)?.identity === tab.id) return null
      moveBetweenWindows(dispatch, getState, current, tab.location)
      return tab.location
    },

    /** «Вкладка» в шапке: положить текущее окно в конец полосы вместе с его работой. */
    createTab: (location) => (dispatch, getState) => {
      const match = matchOf(location)
      const root = getState()
      const state = selectWindows(root)
      if (match === undefined) return false
      if (state.tabs.length >= MAX_TABS || state.tabs.some((tab) => tab.id === match.identity)) return false
      dispatch(
        tabCreated({
          id: match.identity,
          kind: match.descriptor.kind,
          resourceId: match.params.rid ?? null,
          location: withoutStack(location),
          title: windowTitle(match),
          snapshot: match.descriptor.capture?.(root, match.params) ?? null,
          at: now().toISOString(),
        }),
      )
      return true
    },
  }
}
